import { useEffect } from 'react';
import { create } from 'zustand';
import { useStore } from './state/store';
import type { Aircraft, WatchEntry } from './types';
import { EMERGENCY_SQUAWKS, findWatchEntry } from './types';

export type ToastKind = 'watch' | 'emergency';

export interface ToastItem {
  id: string;
  icao24: string;
  message: string;
  kind: ToastKind;
  createdTs: number;
  /** Emergency toasts stay until clicked instead of auto-dismissing. */
  sticky: boolean;
}

interface ToastState {
  toasts: ToastItem[];
  push(t: Omit<ToastItem, 'id' | 'createdTs'>): void;
  dismiss(id: string): void;
}

let toastSeq = 0;

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  push(t) {
    const item: ToastItem = { ...t, id: `toast-${++toastSeq}`, createdTs: Date.now() };
    set((s) => ({ toasts: [...s.toasts, item] }));
  },
  dismiss(id) {
    set((s) => ({ toasts: s.toasts.filter((x) => x.id !== id) }));
  },
}));

/**
 * Request browser notification permission, lazily and only once (skips if
 * already granted/denied). Call only from a user gesture — the watchlist UI
 * calls this the first time a watch entry is added — never on page load.
 */
export function ensureNotificationPermission(): void {
  if (typeof Notification === 'undefined') return;
  if (Notification.permission === 'default') {
    void Notification.requestPermission();
  }
}

function notify(title: string, body: string): void {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  try {
    new Notification(title, { body });
  } catch {
    /* some browsers (e.g. mobile Safari) restrict constructing Notification directly */
  }
}

function fire(icao24: string, message: string, kind: ToastKind, sticky: boolean): void {
  useToastStore.getState().push({ icao24, message, kind, sticky });
  notify(kind === 'emergency' ? 'Emergency squawk' : 'Watchlist alert', message);
}

// An aircraft that flaps in/out of view (or squawk state) shouldn't re-alert
// within this window — see the module doc below.
const DEBOUNCE_MS = 10 * 60 * 1000;
const lastAlertTs = new Map<string, number>();

function shouldAlert(key: string, now: number): boolean {
  const last = lastAlertTs.get(key);
  if (last != null && now - last < DEBOUNCE_MS) return false;
  lastAlertTs.set(key, now);
  return true;
}

interface TrackedState {
  onGround: boolean;
  emergency: boolean;
  lastSeenTs: number;
}
// Per-aircraft last-known state, used to detect onGround/emergency
// transitions. Pruned below so it doesn't grow forever across a long session.
const tracked = new Map<string, TrackedState>();
const TRACKED_TTL_MS = 30 * 60 * 1000;

function label(ac: Aircraft): string {
  return ac.callsign?.trim() || ac.icao24.toUpperCase();
}

function diff(aircraft: Map<string, Aircraft>, watchlist: WatchEntry[]): void {
  const now = Date.now();
  for (const [icao24, ac] of aircraft) {
    const prev = tracked.get(icao24);
    const emergency = ac.squawk != null && EMERGENCY_SQUAWKS.has(ac.squawk);
    const watched = findWatchEntry(ac, watchlist) != null;

    if (!prev) {
      // First time this icao24 has been seen in the live feed this session
      // (or its last sighting aged out) — treat as "appeared".
      if (watched && shouldAlert(`${icao24}:appear`, now)) {
        fire(icao24, `${label(ac)} appeared`, 'watch', false);
      }
      if (emergency && shouldAlert(`${icao24}:emergency`, now)) {
        fire(icao24, `${label(ac)} squawking ${ac.squawk}`, 'emergency', true);
      }
    } else {
      // Debounced like the others: the on-ground bit flaps in real ADS-B data
      // near the runway threshold, and each flip must not re-toast for 10 min.
      if (watched && prev.onGround !== ac.onGround && shouldAlert(`${icao24}:ground`, now)) {
        fire(icao24, `${label(ac)} ${ac.onGround ? 'landed' : 'departed'}`, 'watch', false);
      }
      if (emergency && !prev.emergency && shouldAlert(`${icao24}:emergency`, now)) {
        fire(icao24, `${label(ac)} squawking ${ac.squawk}`, 'emergency', true);
      }
    }

    tracked.set(icao24, { onGround: ac.onGround, emergency, lastSeenTs: now });
  }

  for (const [icao24, t] of tracked) {
    if (now - t.lastSeenTs > TRACKED_TTL_MS) tracked.delete(icao24);
  }
  for (const [key, ts] of lastAlertTs) {
    if (now - ts > DEBOUNCE_MS) lastAlertTs.delete(key);
  }
}

/**
 * Alert engine: diffs successive aircraft states from the store and raises
 * toasts (+ optional browser Notifications) for watched-aircraft
 * appear/land/depart events and any aircraft transitioning into an emergency
 * squawk (7500/7600/7700). Mount once (MapView does, alongside the rest of
 * its live-data wiring) — this subscribes directly to the zustand store
 * rather than hooking into the map's per-tick render loop, so it doesn't
 * touch that loop at all.
 */
export function useAlertEngine(): void {
  useEffect(() => {
    const unsub = useStore.subscribe((s, prev) => {
      if (s.aircraft !== prev.aircraft || s.watchlist !== prev.watchlist) {
        diff(s.aircraft, s.watchlist);
      }
    });
    return unsub;
  }, []);
}
